import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import TritalLogo from "../../components/TritalLogo";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Stethoscope, Building2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome, ${user.name}`);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      <div className="flex flex-col px-6 lg:px-16 py-10">
        <Link to="/"><TritalLogo /></Link>
        <div className="flex-1 flex items-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md mx-auto">
            <div className="overline text-trital-red">Welcome back</div>
            <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl text-slate-900 tracking-tight">Sign in to TRITAL Care®</h1>
            <p className="mt-3 text-slate-600">Access your shifts, jobs, credentials and messages.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="login-email" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Password</label>
                <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="login-password" />
              </div>
              {err && <div className="text-sm text-trital-red bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
              <button disabled={loading} type="submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-60" data-testid="login-submit">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
              </button>
            </form>
            <div className="mt-4 text-sm text-slate-600">
              New here? <Link to="/register" className="text-trital-navy font-semibold hover:underline">Create account</Link>
            </div>
            <div className="mt-8 p-4 rounded-xl bg-slate-50 border border-slate-200">
            </div>
          </motion.div>
        </div>
      </div>
      <div className="hidden lg:block relative bg-trital-navy-mesh">
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="overline text-white/70">Triage Precision. Trusted Workforce.</div>
          <div>
            <h2 className="font-display font-black text-5xl tracking-tight text-balance">The healthcare workforce, precisely staffed.</h2>
            <p className="mt-4 text-white/80 max-w-md">Marketplace, credentialing, and VMS — unified in one enterprise workforce cloud.</p>
          </div>
          <div className="text-sm text-white/60">© {new Date().getFullYear()} TOPTAL Care Inc.</div>
        </div>
      </div>
    </div>
  );
}
